/**
 * Battlefield HP badge rules — authoritative gameState.health is always the source of truth.
 * FX may temporarily show a higher badgeHealth only while a tracked damage session is active.
 */

/** Safety net: no FX session may override the badge longer than this. */
export const MAX_HEALTH_FX_SESSION_MS = 6000;

export function isHealthFxContextActive(instanceId, context = {}) {
  if (!instanceId) {
    return false;
  }
  const { hasPendingDamageTrigger, hasActiveProjectileAnimation } = context;
  return Boolean(
    (typeof hasPendingDamageTrigger === 'function' && hasPendingDamageTrigger(instanceId))
    || (
      typeof hasActiveProjectileAnimation === 'function'
      && hasActiveProjectileAnimation(instanceId)
    )
  );
}

/**
 * Badge number shown on the unit card. Never exceeds authoritative HP unless FX is active.
 * @param {number|undefined} authoritativeHealth - live value from gameState (unit.health)
 * @param {{ displayHealth: number, pulsing?: boolean, startedAt?: number, targetHealth?: number }|null} fxEntry
 */
export function resolveBadgeHealth(authoritativeHealth, fxEntry, context, instanceId) {
  if (typeof authoritativeHealth !== 'number') {
    return authoritativeHealth;
  }
  if (!fxEntry || typeof fxEntry.displayHealth !== 'number') {
    return authoritativeHealth;
  }

  const fxActive =
    fxEntry.pulsing === true
    || isHealthFxContextActive(instanceId, context);

  if (fxEntry.displayHealth > authoritativeHealth) {
    return fxActive ? fxEntry.displayHealth : authoritativeHealth;
  }

  if (fxEntry.pulsing) {
    return fxEntry.displayHealth;
  }

  return authoritativeHealth;
}

/**
 * Drop stale FX sessions whenever live state has moved on without an active FX pipeline.
 */
export function shouldClearHealthFxSession(instanceId, fxEntry, liveHealth, context, now = Date.now()) {
  if (!instanceId || !fxEntry) {
    return false;
  }

  if (typeof fxEntry.startedAt === 'number' && now - fxEntry.startedAt > MAX_HEALTH_FX_SESSION_MS) {
    return true;
  }

  const fxActive =
    fxEntry.pulsing === true
    || isHealthFxContextActive(instanceId, context);

  if (typeof liveHealth !== 'number') {
    return !fxActive;
  }

  if (fxEntry.displayHealth > liveHealth && !fxActive) {
    return true;
  }

  if (
    typeof fxEntry.targetHealth === 'number'
    && liveHealth < fxEntry.targetHealth
    && !fxActive
  ) {
    return true;
  }

  if (fxEntry.displayHealth < liveHealth && !fxEntry.pulsing) {
    return true;
  }

  return false;
}

/**
 * Collect instanceId → health from all battlefield slots in gameState.
 */
export function buildLiveHealthMapFromGameState(gameState) {
  const map = new Map();
  if (!gameState?.state) {
    return map;
  }

  for (const playerState of Object.values(gameState.state)) {
    const playedOrders = playerState?.playedOrders;
    if (!playedOrders) {
      continue;
    }
    for (const zone of ['front', 'flank']) {
      const row = playedOrders[zone];
      if (!Array.isArray(row)) {
        continue;
      }
      for (const unit of row) {
        if (unit?.instanceId && typeof unit.health === 'number') {
          map.set(unit.instanceId, unit.health);
        }
      }
    }
  }

  return map;
}

/** @deprecated Use resolveBadgeHealth — kept for regression tests. */
export function resolveHealthDisplayOverride(unit, override, context = {}) {
  if (!unit?.instanceId || !override || typeof override.health !== 'number') {
    return { apply: false, override: null, stale: false };
  }

  const authoritativeHealth = typeof unit.health === 'number' ? unit.health : undefined;
  const pulsing = override.pulse ?? override.pulsing;
  const badgeHealth = resolveBadgeHealth(
    authoritativeHealth,
    { displayHealth: override.health, pulsing },
    context,
    unit.instanceId
  );

  if (badgeHealth === authoritativeHealth && override.health > authoritativeHealth) {
    const fxActive = isHealthFxContextActive(unit.instanceId, context) || pulsing === true;
    if (!fxActive) {
      return { apply: false, override: null, stale: true };
    }
  }

  if (badgeHealth === authoritativeHealth && !pulsing) {
    return { apply: false, override: null, stale: false };
  }

  return {
    apply: true,
    override: { health: badgeHealth, pulse: pulsing },
    stale: false,
  };
}

export function getOrderBadgeHealth(order) {
  if (!order) {
    return undefined;
  }
  if (typeof order.badgeHealth === 'number') {
    return order.badgeHealth;
  }
  return order.health;
}

/** Returns true when a deploy/strategem projectile is still in flight for this unit. */
export function isInstanceIdInActiveTargetedAnimations(instanceId, animations) {
  if (!instanceId || !Array.isArray(animations)) {
    return false;
  }
  return animations.some((anim) => {
    if (!anim) {
      return false;
    }
    if (anim.targetInstanceId === instanceId) {
      return true;
    }
    if (anim.ghostUnit?.instanceId === instanceId) {
      return true;
    }
    return false;
  });
}
